import React from 'react';
import {
  Box, Button, Popover, TextField, Typography,
} from '@mui/material';
import SearchableMultiSelect from '../../Components/SearchableMultiSelect';
import LeadsProfileFiltersSection from './LeadsProfileFiltersSection';
import {
  LEAD_CHANNEL_FILTER_OPTIONS,
  LEAD_DATE_PRESETS,
  clampLeadDateRange,
  getLeadTodayAR,
  hasLeadDateRangeError,
} from '../leadFilters';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { DOCK_SCROLLBAR_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import { colors } from '../../styles/theme';
import {
  LEADS_DANGER,
  getLeadStageFilterChipSx,
  leadsGhostButtonSx,
  leadsSectionTitleSx,
  leadsSummaryClearButtonSx,
  leadsDateFieldSx,
} from './leadsUi.styles';

const sectionTitleSx = { ...leadsSectionTitleSx, mb: 0.75 };

/**
 * Panel “Filtros” del índice de Leads (popover bajo el botón, como el de
 * Filtros del mapa): última actividad, fecha exacta y canal. El filtro por
 * registro vive en el buscador principal, modo “Registros”. Los cambios se
 * aplican solos (LeadsPanel les pone debounce). Contenido con scroll y fade.
 * Las «Preferencias declaradas» (`if_*` sobre `interest_filters`) se dieron de
 * baja el 20 oct 2026 (docs/maps/leads.md). «Datos del cliente» (`pf_*`) se
 * muestra con `showProfile` (se oculta tras un 503 en la sesión o por flag).
 *
 * @param {{ anchorEl: HTMLElement|null, open: boolean, onClose: () => void,
 *   value: object, onChange: (next: object) => void,
 *   activeCount: number, onClear: () => void, showProfile?: boolean }} props
 */
export default function LeadsFiltersPanel({
  anchorEl, open, onClose, value, onChange, activeCount, onClear, showProfile = false,
}) {
  const set = (patch) => onChange({ ...value, ...patch });
  const today = getLeadTodayAR();
  const dateError = hasLeadDateRangeError(value, today);
  const preset = value.datePreset || '';
  // Atajo ⇄ rango a mano: elegir uno vacía el otro (el atajo no se vuelca a los inputs).
  const pickPreset = (next) => set({ datePreset: next === preset ? '' : next, from: '', to: '' });
  const setDay = (field, v) => set({
    datePreset: '',
    ...clampLeadDateRange({ ...value, [field]: v }, field, today),
  });
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            ...DOCK_SCROLLBAR_SX,
            mt: 0.75,
            width: 'min(360px, 92vw)',
            maxHeight: 'min(80vh, 640px)',
            overflow: 'hidden',
            bgcolor: colors.backgroundSurface,
            color: colors.textPrimary,
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: '10px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <Box
        ref={scrollRef}
        data-testid="leads-filters-scroll"
        sx={{
          ...DOCK_SCROLLBAR_SX,
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '& > *': { flexShrink: 0 },
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Filtros</Typography>
          {activeCount > 0 && (
            <Button size="small" onClick={onClear} sx={leadsSummaryClearButtonSx} aria-label="Limpiar filtros avanzados">
              Limpiar
            </Button>
          )}
        </Box>

        <Box>
          <Typography sx={sectionTitleSx}>Última actividad</Typography>
          <Box role="group" aria-label="Última actividad" sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {LEAD_DATE_PRESETS.map((p) => (
              <Button
                key={p.value}
                size="small"
                aria-pressed={preset === p.value}
                onClick={() => pickPreset(p.value)}
                sx={getLeadStageFilterChipSx(preset === p.value)}
              >
                {p.label}
              </Button>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography sx={sectionTitleSx}>Fecha exacta</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField
              label="Desde"
              type="date"
              size="small"
              value={value.from}
              onChange={(e) => setDay('from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: value.to && value.to < today ? value.to : today, 'aria-label': 'Desde' }}
              error={dateError}
              sx={leadsDateFieldSx}
            />
            <TextField
              label="Hasta"
              type="date"
              size="small"
              value={value.to}
              onChange={(e) => setDay('to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: value.from || undefined, max: today, 'aria-label': 'Hasta' }}
              error={dateError}
              sx={leadsDateFieldSx}
            />
          </Box>
          {dateError && (
            <Typography sx={{ fontSize: '11px', color: LEADS_DANGER, mt: 0.5 }}>Revisá el rango: sin fechas futuras y “Desde” antes que “Hasta”.</Typography>
          )}
        </Box>

        <Box>
          <Typography id="leads-filter-channel-label" sx={sectionTitleSx}>Canal</Typography>
          <SearchableMultiSelect
            id="leads-filter-channel"
            options={LEAD_CHANNEL_FILTER_OPTIONS}
            value={value.channels}
            onChange={(channels) => set({ channels })}
            placeholder="Todos los canales"
            searchPlaceholder="Buscar canal..."
          />
        </Box>

        {showProfile && (
          <LeadsProfileFiltersSection value={value.profile} onChange={(profile) => set({ profile })} />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" variant="outlined" onClick={onClose} sx={leadsGhostButtonSx}>Listo</Button>
        </Box>
      </Box>
    </Popover>
  );
}
